import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  BrainCircuit,
  CheckCircle2,
  Database,
  Layers3,
  Loader2,
  PenLine,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, type AgentTraceEvent } from "@/lib/api";
import { cn } from "@/lib/cn";

const AGENTS = [
  { key: "Planner", icon: BrainCircuit, blurb: "Designs the blueprint" },
  { key: "Retriever", icon: Database, blurb: "RAG over 3,900+ items" },
  { key: "Generator", icon: PenLine, blurb: "Authors fresh questions" },
  { key: "Verifier", icon: CheckCircle2, blurb: "Checks every answer key" },
  { key: "Assembler", icon: Layers3, blurb: "Collates your exam" },
] as const;

type Mix = { quant: number; verbal: number; di: number };

const PRESETS: { id: string; label: string; mix: Mix }[] = [
  { id: "balanced", label: "Balanced · 9", mix: { quant: 3, verbal: 3, di: 3 } },
  { id: "full", label: "Full mock · 12", mix: { quant: 4, verbal: 4, di: 4 } },
  { id: "quant", label: "Quant only · 6", mix: { quant: 6, verbal: 0, di: 0 } },
  { id: "verbal", label: "Verbal only · 6", mix: { quant: 0, verbal: 6, di: 0 } },
  { id: "di", label: "Data Insights only · 6", mix: { quant: 0, verbal: 0, di: 6 } },
];

export function AgentRunPanel({ onStart }: { onStart: (sessionId: string) => void }) {
  const [preset, setPreset] = useState(PRESETS[0]);
  const [aiAuthor, setAiAuthor] = useState(false);
  const [running, setRunning] = useState(false);
  const [trace, setTrace] = useState<AgentTraceEvent[]>([]);
  const [visibleCount, setVisibleCount] = useState(0);
  const [activeAgent, setActiveAgent] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const timers = useRef<number[]>([]);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const run = async () => {
    setRunning(true);
    setError(false);
    setTrace([]);
    setVisibleCount(0);
    setSessionId(null);
    setActiveAgent("Planner");
    try {
      const mix = preset.mix;
      const sections = (["quant", "verbal", "di"] as const)
        .filter((s) => mix[s] > 0)
        .map((s) => ({ id: s, count: mix[s] }));
      const res = await api.agentGenerateMock(sections, aiAuthor);
      setTrace(res.trace);
      setSessionId(res.session_id);
      // Reveal trace events one-by-one for the "agents at work" effect.
      res.trace.forEach((ev, i) => {
        const t = window.setTimeout(() => {
          setVisibleCount(i + 1);
          setActiveAgent(ev.agent);
          if (i === res.trace.length - 1) {
            setActiveAgent(null);
            setRunning(false);
          }
        }, 160 * (i + 1));
        timers.current.push(t);
      });
    } catch {
      setError(true);
      setRunning(false);
      setActiveAgent(null);
    }
  };

  const done = sessionId && visibleCount >= trace.length && trace.length > 0;

  return (
    <div className="grain relative overflow-hidden rounded-[28px] border border-primary/25 bg-gradient-to-br from-background via-card to-primary/5 p-8 shadow-editorial-lg md:p-10">
      <div
        aria-hidden
        className="pointer-events-none absolute -right-20 -top-20 size-72 rounded-full bg-primary/15 blur-3xl"
      />
      <div className="relative">
        <div className="flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-muted-foreground">
          <span className="hero-rule" />
          <Sparkles className="size-3.5 text-accent" /> Agentic exam builder
        </div>
        <h2 className="mt-3 font-serif text-3xl leading-tight md:text-4xl">
          Let the agents build your mock.
        </h2>
        <p className="mt-2 max-w-xl text-sm text-muted-foreground">
          Five specialist agents plan, retrieve from a 3,900+ item bank with RAG,
          author fresh questions with the fine-tuned generators, and verify every
          answer key — live.
        </p>

        {/* Agent row */}
        <div className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-5">
          {AGENTS.map((a) => {
            const isActive = activeAgent === a.key;
            const hasRun = trace.some((t) => t.agent === a.key && visibleCount > 0);
            return (
              <div
                key={a.key}
                className={cn(
                  "relative flex flex-col gap-1 rounded-2xl border p-3 transition-all",
                  isActive
                    ? "border-primary/50 bg-primary/10 shadow-editorial"
                    : hasRun
                      ? "border-success/40 bg-success/5"
                      : "border-border/60 bg-card/50",
                )}
              >
                <div className="flex items-center justify-between">
                  <a.icon
                    className={cn(
                      "size-4",
                      isActive ? "text-primary" : hasRun ? "text-success" : "text-muted-foreground",
                    )}
                  />
                  {isActive && <Loader2 className="size-3 animate-spin text-primary" />}
                  {hasRun && !isActive && <CheckCircle2 className="size-3 text-success" />}
                </div>
                <div className="text-xs font-medium">{a.key}</div>
                <div className="text-[10px] leading-tight text-muted-foreground">{a.blurb}</div>
              </div>
            );
          })}
        </div>

        {/* Controls */}
        <div className="mt-6 flex flex-wrap items-center gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              disabled={running}
              onClick={() => setPreset(p)}
              className={cn(
                "rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors",
                preset.id === p.id
                  ? "border-foreground bg-foreground text-background"
                  : "border-border/60 bg-card/60 text-muted-foreground hover:text-foreground",
              )}
            >
              {p.label}
            </button>
          ))}
          <button
            type="button"
            disabled={running}
            onClick={() => setAiAuthor((v) => !v)}
            title="Author brand-new questions with the fine-tuned generators (needs the GPU LoRA endpoint; slower)"
            className={cn(
              "flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
              aiAuthor
                ? "border-accent/50 bg-accent/10 text-accent-foreground"
                : "border-border/60 bg-card/60 text-muted-foreground hover:text-foreground",
            )}
          >
            <BrainCircuit className="size-3.5" />
            AI-author {aiAuthor ? "on" : "off"}
          </button>
          <div className="ml-auto flex items-center gap-2">
            {!done ? (
              <Button onClick={run} disabled={running} variant="default">
                {running ? (
                  <>
                    <Loader2 className="size-4 animate-spin" /> Agents working…
                  </>
                ) : (
                  <>
                    <Sparkles className="size-4" /> Generate mock
                  </>
                )}
              </Button>
            ) : (
              <Button variant="default" onClick={() => sessionId && onStart(sessionId)}>
                Start the exam →
              </Button>
            )}
          </div>
        </div>

        {error && (
          <p className="mt-4 text-sm text-destructive">
            Could not reach the agent service. Make sure the API is deployed/running.
          </p>
        )}

        {/* Live trace */}
        <AnimatePresence>
          {trace.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="mt-6 space-y-1.5 rounded-2xl border border-border/60 bg-background/60 p-4"
            >
              {trace.slice(0, visibleCount).map((ev, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-start gap-2 font-mono text-[11px]"
                >
                  <span className="shrink-0 rounded bg-secondary px-1.5 py-0.5 text-[10px] font-medium text-foreground">
                    {ev.agent}
                  </span>
                  <span className="text-muted-foreground">{ev.detail}</span>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
